import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ConfirmModal,
} from '@heyroot/backoffice-ui';
import { useAdminAuth } from '../../lib/auth-context.js';
import { PERMS } from '../../../rbac/permissions.js';
import { authenticatedFetch } from '../../lib/authenticated-fetch.js';

// ─── Types ───────────────────────────────────────────────────────────────────

interface OnboardingStep {
  stepKey: string;
  status: string;
  completedAt: string | null;
  blockedReason: string | null;
  sortOrder: number;
}

// Staff-facing copy for the CardPointe production-application step
// (payments.production_application) -- staff are authorized to see the raw
// blockedReason text below; the merchant-facing surface (Task 8) never
// shows it. Other step keys keep the raw status string.
const PRODUCTION_APPLICATION_COPY: Record<string, string> = {
  not_started: 'Request a merchant account',
  in_progress: 'Application under review',
  blocked: 'Application needs attention',
  completed: 'Merchant account approved',
};

interface OnboardingClaim {
  adminUid: string;
  adminEmail: string;
  expiresAt: string;
}

interface OnboardingSnooze {
  snoozeUntil: string;
  reason: string;
}

interface OnboardingDetail {
  tenants: {
    id: string;
    onboardingGoliveCompletedAt: string | null;
    isFinalized?: unknown;
    onboardingState?: unknown;
  };
  tenantOnboardingClaims: OnboardingClaim | null;
  tenantOnboardingSnooze?: OnboardingSnooze | null;
  steps: OnboardingStep[];
}

// ─── Query key ───────────────────────────────────────────────────────────────

export const onboardingDetailKey = (tenantId: string) => ['onboarding-detail', tenantId] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function fetchOnboardingDetail(tenantId: string): Promise<OnboardingDetail> {
  const res = await authenticatedFetch(`/api/onboarding/${encodeURIComponent(tenantId)}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      error?: { message?: string };
    };
    throw new Error(body.message ?? body.error?.message ?? `Request failed: ${res.status}`);
  }
  // Root-api wraps responses as { data: ..., requestId: string }; unwrap the envelope.
  const envelope = (await res.json()) as { data: OnboardingDetail; requestId: string };
  return envelope.data;
}

async function mutateOnboarding(
  tenantId: string,
  method: string,
  subpath: string,
  body?: unknown,
): Promise<void> {
  const serialized = body !== undefined ? JSON.stringify(body) : null;
  const res = await authenticatedFetch(
    `/api/onboarding/${encodeURIComponent(tenantId)}${subpath}`,
    {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: serialized,
    },
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(json.message ?? `Request failed: ${res.status}`);
  }
}

const RESCUE_KINDS = [
  'onboarding.rescue.partial.day2',
  'onboarding.rescue.dormant.day2',
  'onboarding.rescue.day7',
  'onboarding.rescue.day10',
  'onboarding.rescue.day14',
] as const;
type RescueKind = (typeof RESCUE_KINDS)[number];

// ─── Sub-components ──────────────────────────────────────────────────────────

// These are persisted facts, not an eligibility or authorization decision.
// A missing response field or malformed record must not look like an absent enrollment.
function signupRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null) return {};
  return typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

const signupTimestampSchema = z.iso.datetime({ offset: true });
const signupTimestampFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'long',
  timeZone: 'UTC',
});

function signupTimestamp(record: Record<string, unknown> | undefined, key: string): ReactNode {
  if (!record) return 'Unknown';
  const value = record[key];
  if (value == null) return 'Not recorded';
  const parsed = signupTimestampSchema.safeParse(value);
  if (!parsed.success) return 'Unknown';
  return <time dateTime={parsed.data}>{signupTimestampFormat.format(new Date(parsed.data))}</time>;
}

function SignupSetupSummary({ tenant }: { tenant: OnboardingDetail['tenants'] }) {
  const headingId = useId();
  const state =
    tenant.onboardingState === undefined ? undefined : signupRecord(tenant.onboardingState);
  const entry = state ? signupRecord(state['entrySetup']) : undefined;
  const version = entry?.['version'];
  const rows: Array<{ label: string; value: ReactNode }> = [
    {
      label: 'Finalized',
      value:
        typeof tenant.isFinalized === 'boolean' ? (tenant.isFinalized ? 'Yes' : 'No') : 'Unknown',
    },
    { label: 'Wizard completed', value: signupTimestamp(state, 'meta_wizard_completed_at') },
    {
      label: 'Entry setup version',
      value: !entry
        ? 'Unknown'
        : version == null
          ? 'Not recorded'
          : typeof version === 'string' && version.trim()
            ? version
            : 'Unknown',
    },
    { label: 'Entry enrolled', value: signupTimestamp(entry, 'enrolledAt') },
    { label: 'Entry completed', value: signupTimestamp(entry, 'completedAt') },
  ];

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-card border border-border bg-surface p-4"
    >
      <h3 id={headingId} className="text-heading-3 text-primary">
        Signup setup
      </h3>
      <p className="mt-1 text-body-sm text-secondary">
        Saved signup records. Day-two onboarding progress is shown separately below.
      </p>
      <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map(({ label, value }) => (
          <div key={label} className="min-w-0">
            <dt className="text-caption text-secondary">{label}</dt>
            <dd className="m-0 mt-1 break-words text-body-sm text-primary tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

interface BlockedChainEntry {
  stepKey: string;
  label: string;
  status: string;
  href: string;
}
interface ExplainResult {
  stepKey: string;
  status: string;
  blockedBy: BlockedChainEntry[];
  explanation: string;
}

function BlockedChainView({ tenantId, stepKey }: { tenantId: string; stepKey: string }) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'success'; result: ExplainResult }
  >({ kind: 'idle' });

  async function handleClick() {
    setState({ kind: 'loading' });
    try {
      const res = await authenticatedFetch(
        `/api/onboarding/${encodeURIComponent(tenantId)}/steps/${encodeURIComponent(stepKey)}/explain`,
      );
      if (!res.ok) throw new Error(`explain failed: ${res.status}`);
      const body = (await res.json()) as { data: ExplainResult; requestId: string };
      setState({ kind: 'success', result: body.data });
    } catch {
      setState({ kind: 'error', message: "Couldn't load the explanation." });
    }
  }

  if (state.kind === 'idle') {
    return (
      <Button size="sm" variant="ghost" onClick={() => void handleClick()}>
        Why is this blocked?
      </Button>
    );
  }
  if (state.kind === 'loading') {
    return (
      <span role="status" className="text-body-sm text-muted">
        Loading…
      </span>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-body-sm text-danger-text">{state.message}</span>
        <Button size="sm" variant="ghost" onClick={() => void handleClick()}>
          Why is this blocked?
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 rounded-control border border-border bg-surface p-3">
      <p className="text-body-sm text-primary">{state.result.explanation}</p>
      {state.result.blockedBy.length > 0 && (
        <ol className="list-decimal pl-5 text-body-sm text-secondary">
          {state.result.blockedBy.map((entry) => (
            <li key={entry.stepKey}>
              {entry.label} ({entry.status})
            </li>
          ))}
        </ol>
      )}
      <Button size="sm" variant="ghost" onClick={() => setState({ kind: 'idle' })}>
        Dismiss
      </Button>
    </div>
  );
}

function StepProgress({ steps, tenantId }: { steps: OnboardingStep[]; tenantId: string }) {
  const sorted = [...steps].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5">
      <h3 className="text-eyebrow uppercase text-muted mb-3">Onboarding Progress</h3>
      <div className="flex flex-col gap-0.5">
        {sorted.map((step) => (
          <div key={step.stepKey} className="flex flex-col gap-1 py-1">
            <div className="flex items-center gap-3">
              <span
                className={step.status === 'completed' ? 'text-success-text' : 'text-muted'}
                aria-hidden="true"
              >
                {step.status === 'completed' ? '✓' : '○'}
              </span>
              <span className="font-mono text-body-sm">{step.stepKey}</span>
              <Badge variant={step.status === 'completed' ? 'success' : 'neutral'} className="ml-1">
                {step.status}
              </Badge>
              {step.stepKey === 'payments.production_application' && (
                <span className="text-body-sm text-muted">
                  {PRODUCTION_APPLICATION_COPY[step.status] ?? step.status}
                </span>
              )}
              {step.stepKey === 'payments.production_application' &&
                step.status === 'blocked' &&
                step.blockedReason && (
                  <span className="text-caption text-warning-text ml-2">{step.blockedReason}</span>
                )}
              {step.completedAt && (
                <span className="text-muted text-caption ml-auto tabular-nums">
                  {new Date(step.completedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {step.status === 'blocked' && (
              <div className="ml-7">
                <BlockedChainView tenantId={tenantId} stepKey={step.stepKey} />
              </div>
            )}
            <div className="ml-7 flex items-center gap-2">
              <StepOverrideButton
                tenantId={tenantId}
                stepKey={step.stepKey}
                action="force-complete"
              />
              <StepOverrideButton tenantId={tenantId} stepKey={step.stepKey} action="reopen" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Client-side mirror of modules/tenants/api-interface/tenants.ts's
// isAdminProtectedStep -- UX-only (disable the button before a wasted
// round-trip); the server remains authoritative via its own STEP_PROTECTED
// check regardless of this client-side gate.
const PROTECTED_STEP_PREFIXES = ['financial.tax', 'payments.', 'golive.'];
const PROTECTED_STEP_EXACT = new Set(['financial.coa']);
function isProtectedStep(stepKey: string): boolean {
  return (
    PROTECTED_STEP_EXACT.has(stepKey) || PROTECTED_STEP_PREFIXES.some((p) => stepKey.startsWith(p))
  );
}

function ResyncButton({ tenantId }: { tenantId: string }) {
  const { hasPerm } = useAdminAuth();
  const [busy, setBusy] = useState(false);
  // Inline error/success feedback -- apps/admin has no <ToastProvider>
  // mounted anywhere, so a toast() call would silently queue and never
  // render. Mirrors BlockedChainView's state-machine pattern (Task 12).
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; message: string } | null>(
    null,
  );

  if (!hasPerm(PERMS.ONBOARDING_RESYNC)) return null;

  async function handleResync() {
    setBusy(true);
    setFeedback(null);
    try {
      const res = await authenticatedFetch(`/api/onboarding/${tenantId}/resync`, {
        method: 'POST',
      });
      if (res.status === 409) {
        // RESYNC_IN_PROGRESS -- stay disabled, not a raw error.
        return;
      }
      if (!res.ok) throw new Error(`resync failed: ${res.status}`);
      setFeedback({ kind: 'success', message: 'Resync complete.' });
    } catch {
      setFeedback({ kind: 'error', message: "Couldn't resync this tenant." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={() => void handleResync()} disabled={busy}>
        Resync
      </Button>
      {feedback && (
        <span role={feedback.kind === 'error' ? 'alert' : 'status'} className="text-body-sm">
          {feedback.message}
        </span>
      )}
    </div>
  );
}

function StepOverrideButton({
  tenantId,
  stepKey,
  action,
}: {
  tenantId: string;
  stepKey: string;
  action: 'force-complete' | 'reopen';
}) {
  const { hasPerm } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; message: string } | null>(
    null,
  );
  const protectedForAction = action === 'force-complete' && isProtectedStep(stepKey);
  const actionLabel = action === 'force-complete' ? 'force-complete' : 'reopen';

  if (!hasPerm(PERMS.ONBOARDING_STEP_OVERRIDE)) return null;

  async function handleConfirm(reason: string) {
    if (submitting) return; // guards against a double-submit while the request is in flight
    setSubmitting(true);
    try {
      const res = await authenticatedFetch(
        `/api/onboarding/${tenantId}/steps/${stepKey}/${action}`,
        {
          method: 'POST',
          body: JSON.stringify({ reason }),
        },
      );
      if (!res.ok) throw new Error(`${action} failed: ${res.status}`);
      // Success closes the modal and surfaces inline confirmation -- failure
      // (below) intentionally leaves the modal open so the reason isn't lost.
      setOpen(false);
      setFeedback({
        kind: 'success',
        message: action === 'force-complete' ? 'Step force-completed.' : 'Step reopened.',
      });
    } catch {
      setFeedback({ kind: 'error', message: `Couldn't ${actionLabel} this step.` });
    } finally {
      setSubmitting(false);
    }
  }

  const label = action === 'force-complete' ? `Force-complete ${stepKey}` : `Reopen ${stepKey}`;

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setFeedback(null);
          setOpen(true);
        }}
        disabled={protectedForAction}
        title={protectedForAction ? 'Protected step -- cannot be force-completed' : undefined}
      >
        {label}
      </Button>
      <ConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => {}}
        onConfirmWithReason={(reason) => void handleConfirm(reason)}
        reasonRequired
        reasonMinLength={1}
        reasonMaxLength={500}
        isLoading={submitting}
        title={action === 'force-complete' ? `Force-complete ${stepKey}?` : `Reopen ${stepKey}?`}
      />
      {feedback && (
        <span role={feedback.kind === 'error' ? 'alert' : 'status'} className="text-body-sm">
          {feedback.message}
        </span>
      )}
    </div>
  );
}

function ClaimPanel({
  tenantId,
  claim,
  snooze,
  onRefresh,
}: {
  tenantId: string;
  claim: OnboardingClaim | null;
  snooze: OnboardingSnooze | null | undefined;
  onRefresh: () => void;
}) {
  const { hasPerm } = useAdminAuth();
  const canClaim = hasPerm(PERMS.ONBOARDING_CLAIM);
  const canOverride = hasPerm(PERMS.ONBOARDING_OVERRIDE_CLAIM);
  const canSnooze = hasPerm(PERMS.ONBOARDING_SNOOZE);

  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeReason, setSnoozeReason] = useState('');
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      onRefresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  function validateSnooze(): string | null {
    if (!snoozeDate) return 'Snooze date is required';
    if (!snoozeReason.trim()) return 'Reason is required';
    if (snoozeReason.trim().length > 500) return 'Reason must be 500 characters or fewer';
    const until = new Date(snoozeDate);
    const max = new Date();
    max.setDate(max.getDate() + 30);
    if (until > max) return 'Snooze date must be within 30 days';
    return null;
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-4">
      <h3 className="text-eyebrow uppercase text-muted">Claim &amp; Snooze</h3>

      {/* Snooze status */}
      {snooze && (
        <div className="flex items-center gap-3 rounded-control border border-border bg-surface-sunken p-3">
          <Badge variant="warning">Snoozed</Badge>
          <span className="text-body-sm text-secondary">
            Until {new Date(snooze.snoozeUntil).toLocaleDateString()} — {snooze.reason}
          </span>
          {canSnooze && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              className="ml-auto"
              onClick={() => void run(() => mutateOnboarding(tenantId, 'DELETE', '/snooze', {}))}
            >
              Unsnooze
            </Button>
          )}
        </div>
      )}

      {/* Claim status */}
      {claim === null ? (
        canClaim ? (
          <div className="flex items-center gap-2">
            <span className="text-body-sm text-muted">Unclaimed</span>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void run(() => mutateOnboarding(tenantId, 'POST', '/claim', {}))}
            >
              Claim
            </Button>
          </div>
        ) : (
          <span className="text-body-sm text-muted">Unclaimed</span>
        )
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="info">Claimed</Badge>
            <span className="text-body-sm text-secondary">
              by {claim.adminEmail} — expires {new Date(claim.expiresAt).toLocaleString()}
            </span>
            {canClaim && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() =>
                    void run(() => mutateOnboarding(tenantId, 'POST', '/claim/renew', {}))
                  }
                >
                  Renew
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    void run(() =>
                      mutateOnboarding(tenantId, 'DELETE', '/claim', { override: false }),
                    )
                  }
                >
                  Release
                </Button>
              </>
            )}
            {!canClaim && canOverride && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void run(() => mutateOnboarding(tenantId, 'DELETE', '/claim', { override: true }))
                }
              >
                Override release
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Snooze form */}
      {canSnooze && !snooze && (
        <div className="border-t border-border pt-4 flex flex-col gap-2">
          <p className="text-eyebrow uppercase text-muted">Set Snooze</p>
          <div className="flex items-start gap-2 flex-wrap">
            <Input
              type="date"
              value={snoozeDate}
              onChange={(e) => setSnoozeDate(e.target.value)}
              aria-label="Snooze until date"
            />
            <div className="flex-1 min-w-[200px]">
              <Textarea
                value={snoozeReason}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSnoozeReason(e.target.value)}
                placeholder="Reason (required)"
                rows={2}
                aria-label="Snooze reason"
              />
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                const err = validateSnooze();
                if (err) {
                  setSnoozeError(err);
                  return;
                }
                setSnoozeError(null);
                void run(() =>
                  mutateOnboarding(tenantId, 'POST', '/snooze', {
                    until: snoozeDate,
                    reason: snoozeReason.trim(),
                  }),
                );
              }}
            >
              Snooze
            </Button>
          </div>
          {snoozeError && <p className="text-caption text-danger-text">{snoozeError}</p>}
        </div>
      )}

      {actionError && <p className="text-body-sm text-danger-text">{actionError}</p>}
    </section>
  );
}

function InterventionPanel({
  tenantId,
  isLive,
  onRefresh,
}: {
  tenantId: string;
  isLive: boolean;
  onRefresh: () => void;
}) {
  const { hasPerm } = useAdminAuth();
  const canMarkLive = hasPerm(PERMS.ONBOARDING_MARK_LIVE);
  const canRescue = hasPerm(PERMS.ONBOARDING_RESCUE_EMAIL);

  const [markLiveOpen, setMarkLiveOpen] = useState(false);
  const [markLiveReason, setMarkLiveReason] = useState('');
  const [markLiveBusy, setMarkLiveBusy] = useState(false);
  const [markLiveError, setMarkLiveError] = useState<string | null>(null);

  const [rescueKind, setRescueKind] = useState<RescueKind>('onboarding.rescue.day7');
  const [rescueReason, setRescueReason] = useState('');
  const [rescueBusy, setRescueBusy] = useState(false);
  const [rescueError, setRescueError] = useState<string | null>(null);
  const [rescueSent, setRescueSent] = useState(false);
  // Stable idempotency key per send intent — rotated after each success so retries
  // within the same attempt reuse the same key (preventing duplicate emails),
  // but distinct sends get distinct keys. conv-idempotency-keys.md: time-based keys banned.
  const [rescueIdempotencyKey, setRescueIdempotencyKey] = useState(() => crypto.randomUUID());

  async function handleMarkLive() {
    if (!markLiveReason.trim()) {
      setMarkLiveError('Reason is required.');
      return;
    }
    setMarkLiveBusy(true);
    setMarkLiveError(null);
    try {
      await mutateOnboarding(tenantId, 'POST', '/mark-live', { reason: markLiveReason });
      setMarkLiveOpen(false);
      setMarkLiveReason('');
      onRefresh();
    } catch (e) {
      setMarkLiveError(e instanceof Error ? e.message : 'Failed to mark as live');
    } finally {
      setMarkLiveBusy(false);
    }
  }

  async function handleRescueEmail() {
    if (!rescueReason.trim()) {
      setRescueError('Reason is required.');
      return;
    }
    setRescueBusy(true);
    setRescueError(null);
    setRescueSent(false);
    try {
      await mutateOnboarding(tenantId, 'POST', '/rescue-email', {
        rescueKind,
        idempotencyKey: rescueIdempotencyKey,
        reason: rescueReason.trim(),
      });
      setRescueSent(true);
      setRescueReason('');
      setRescueIdempotencyKey(crypto.randomUUID());
    } catch (e) {
      setRescueError(e instanceof Error ? e.message : 'Failed to send rescue email');
    } finally {
      setRescueBusy(false);
    }
  }

  return (
    <section className="rounded-control border border-border bg-surface-elevated p-5 flex flex-col gap-5">
      <h3 className="text-eyebrow uppercase text-muted">Interventions</h3>

      {/* Mark as Live */}
      <div className="flex flex-col gap-2">
        <p className="text-body-sm-emphasis text-primary">Mark as Live</p>
        {isLive ? (
          <p className="text-body-sm text-success-text">Tenant is already live.</p>
        ) : canMarkLive ? (
          <>
            <p className="text-body-sm text-secondary">
              Override the normal go-live flow and mark this tenant as live.
            </p>
            <div>
              <Button variant="secondary" size="sm" onClick={() => setMarkLiveOpen(true)}>
                Mark as Live
              </Button>
            </div>
            <ConfirmModal
              open={markLiveOpen}
              onClose={() => {
                setMarkLiveOpen(false);
                setMarkLiveError(null);
              }}
              onConfirm={() => void handleMarkLive()}
              title="Mark tenant as live?"
              confirmLabel="Mark as Live"
              isLoading={markLiveBusy}
            >
              <div className="flex flex-col gap-3">
                <p className="text-body-sm text-secondary">
                  This overrides the normal go-live flow. The tenant will be marked live
                  immediately.
                </p>
                <Textarea
                  value={markLiveReason}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setMarkLiveReason(e.target.value)
                  }
                  placeholder="Reason (required)"
                  rows={3}
                  aria-label="Mark as live reason"
                />
                {markLiveError && <p className="text-body-sm text-danger-text">{markLiveError}</p>}
              </div>
            </ConfirmModal>
          </>
        ) : (
          <p className="text-body-sm text-muted">
            You do not have permission to mark this tenant as live.
          </p>
        )}
      </div>

      {/* Rescue Email */}
      {canRescue && (
        <div className="border-t border-border pt-4 flex flex-col gap-2">
          <p className="text-body-sm-emphasis text-primary">Rescue Email</p>
          <div className="flex items-start gap-2 flex-wrap">
            <div className="w-44">
              <Select value={rescueKind} onValueChange={(v) => setRescueKind(v as RescueKind)}>
                <SelectTrigger disabled={rescueBusy} aria-label="Rescue email kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESCUE_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Textarea
                value={rescueReason}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setRescueReason(e.target.value)}
                placeholder="Reason (required)"
                rows={2}
                disabled={rescueBusy}
                aria-label="Rescue email reason"
              />
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={rescueBusy}
              onClick={() => void handleRescueEmail()}
            >
              {rescueBusy ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {rescueSent && <p className="text-body-sm text-success-text">Email queued.</p>}
          {rescueError && <p className="text-body-sm text-danger-text">{rescueError}</p>}
        </div>
      )}
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function OnboardingTab({ tenantId }: { tenantId: string }) {
  const { hasPerm } = useAdminAuth();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error } = useQuery({
    queryKey: onboardingDetailKey(tenantId),
    queryFn: () => fetchOnboardingDetail(tenantId),
    enabled: hasPerm(PERMS.ONBOARDING_VIEW),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: onboardingDetailKey(tenantId) });
  }

  if (!hasPerm(PERMS.ONBOARDING_VIEW)) {
    return (
      <p className="text-body-sm text-muted py-4">
        You do not have permission to view onboarding details.
      </p>
    );
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-4 py-2">
        <Skeleton className="h-40 w-full rounded-control" />
        <Skeleton className="h-32 w-full rounded-control" />
        <Skeleton className="h-32 w-full rounded-control" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
      >
        {error instanceof Error ? error.message : 'Failed to load onboarding details.'}
      </div>
    );
  }

  if (!data) return null;

  const isLive = Boolean(data.tenants.onboardingGoliveCompletedAt);

  return (
    <div className="flex flex-col gap-4 py-2">
      {hasPerm(PERMS.ONBOARDING_AUDIT_VIEW) && (
        <a
          href={`/tenants/${encodeURIComponent(tenantId)}/onboarding/events`}
          className="inline-flex min-h-11 items-center self-start text-body-sm text-secondary underline underline-offset-4 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus rounded-control"
        >
          View onboarding history
        </a>
      )}
      <SignupSetupSummary tenant={data.tenants} />
      <StepProgress steps={data.steps} tenantId={tenantId} />
      <ClaimPanel
        tenantId={tenantId}
        claim={data.tenantOnboardingClaims}
        snooze={data.tenantOnboardingSnooze}
        onRefresh={refresh}
      />
      <ResyncButton tenantId={tenantId} />
      <InterventionPanel tenantId={tenantId} isLive={isLive} onRefresh={refresh} />
    </div>
  );
}
